/**
 * Browser-based office folder sync (Chrome / Edge).
 * User picks the shared folder once; the handle is stored in IndexedDB on this PC.
 * No background scripts — sync runs when they click Sync or open the Files tab.
 */

import axios from 'axios';
import { API } from '../config';

const DB_NAME = 'vmk-network-sync-v1';
const STORE = 'folder-handle';
const AUTO_SYNC_KEY = 'vmk-network-auto-sync';

const HIDDEN = new Set(['$recycle.bin', 'system volume information', 'thumbs.db', 'desktop.ini', '.sync-meta.json']);
const ALLOWED_SUFFIXES = new Set([
  '.pdf', '.png', '.jpg', '.jpeg', '.webp', '.gif', '.doc', '.docx', '.xls', '.xlsx', '.txt', '.csv', '.zip', '.msg',
]);
const MAX_BYTES = 40 * 1024 * 1024;

export function isBrowserFolderSyncSupported() {
  return typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';
}

export function getAutoSyncPreference() {
  try {
    const v = localStorage.getItem(AUTO_SYNC_KEY);
    return v !== 'false';
  } catch {
    return true;
  }
}

export function setAutoSyncPreference(enabled) {
  try {
    localStorage.setItem(AUTO_SYNC_KEY, enabled ? 'true' : 'false');
  } catch {
    /* ignore */
  }
}

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE);
    };
  });
}

export async function saveLocalFolderHandle(handle) {
  const db = await openDb();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put({ handle, name: handle.name, savedAt: Date.now() }, 'root');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function loadLocalFolderHandle() {
  const db = await openDb();
  const record = await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get('root');
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
  db.close();
  return record;
}

export async function clearLocalFolderHandle() {
  const db = await openDb();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete('root');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function pickLocalNetworkFolder() {
  if (!isBrowserFolderSyncSupported()) {
    throw new Error('UNSUPPORTED_BROWSER');
  }
  const handle = await window.showDirectoryPicker({ mode: 'read' });
  await saveLocalFolderHandle(handle);
  return handle;
}

export async function ensureFolderPermission(handle) {
  let perm = await handle.queryPermission({ mode: 'read' });
  if (perm === 'granted') return true;
  perm = await handle.requestPermission({ mode: 'read' });
  return perm === 'granted';
}

async function listTopLevelDirs(rootHandle) {
  const out = [];
  // eslint-disable-next-line no-restricted-syntax
  for await (const [name, entry] of rootHandle.entries()) {
    if (entry.kind !== 'directory') continue;
    if (name.startsWith('.') || HIDDEN.has(name.toLowerCase())) continue;
    out.push({ name, handle: entry });
  }
  out.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  return out;
}

async function collectFiles(dirHandle, topName, relInside = '') {
  const files = [];
  // eslint-disable-next-line no-restricted-syntax
  for await (const [name, entry] of dirHandle.entries()) {
    if (name.startsWith('.') || HIDDEN.has(name.toLowerCase())) continue;
    const rel = relInside ? `${relInside}/${name}` : name;
    if (entry.kind === 'directory') {
      const nested = await collectFiles(entry, topName, rel);
      files.push(...nested);
    } else if (entry.kind === 'file') {
      const dot = name.lastIndexOf('.');
      const suf = dot >= 0 ? name.slice(dot).toLowerCase() : '';
      if (suf && !ALLOWED_SUFFIXES.has(suf)) continue;
      try {
        const file = await entry.getFile();
        if (file.size > MAX_BYTES) continue;
        files.push({ relPath: `${topName}/${rel}`, file });
      } catch {
        /* skip unreadable */
      }
    }
  }
  return files;
}

async function uploadOne(relPath, file, agentLabel) {
  const form = new FormData();
  form.append('path', relPath);
  form.append('upload', file);
  form.append('agent', agentLabel);
  await axios.post(`${API}/network-files/sync/upload`, form, {
    withCredentials: true,
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000,
  });
}

async function folderDone(folderName, count, agentLabel) {
  const form = new FormData();
  form.append('folder', folderName);
  form.append('files_uploaded', String(count));
  form.append('agent', agentLabel);
  await axios.post(`${API}/network-files/sync/folder-done`, form, {
    withCredentials: true,
    timeout: 120000,
  });
}

/**
 * @param {FileSystemDirectoryHandle} rootHandle
 * @param {(progress: {phase: string, folder?: string, current?: number, total?: number}) => void} onProgress
 */
export async function syncLocalFolderToServer(rootHandle, onProgress) {
  const agentLabel = `browser@${typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 40) : 'web'}`;

  onProgress?.({ phase: 'manifest' });
  const manifestRes = await axios.get(`${API}/network-files/sync/manifest`, { withCredentials: true });
  const serverFolders = new Set(manifestRes.data?.folders || []);

  const localFolders = await listTopLevelDirs(rootHandle);
  const toSync =
    serverFolders.size === 0
      ? localFolders
      : localFolders.filter((f) => !serverFolders.has(f.name));

  if (!toSync.length) {
    return { uploaded: 0, folders: 0, skipped: true };
  }

  let uploaded = 0;
  for (const { name, handle } of toSync) {
    const fresh = await axios.get(`${API}/network-files/sync/manifest`, { withCredentials: true });
    const onServer = new Set(fresh.data?.folders || []);
    if (onServer.has(name)) continue;

    onProgress?.({ phase: 'folder', folder: name });
    const files = await collectFiles(handle, name);
    let folderCount = 0;
    for (let i = 0; i < files.length; i += 1) {
      const { relPath, file } = files[i];
      onProgress?.({ phase: 'file', folder: name, current: i + 1, total: files.length });
      await uploadOne(relPath, file, agentLabel);
      uploaded += 1;
      folderCount += 1;
    }
    await folderDone(name, folderCount, agentLabel);
  }

  return { uploaded, folders: toSync.length, skipped: false };
}
